import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling, withNavigationErrorHandler, withRouterConfig } from '@angular/router';
import { provideGoogleMaps } from '@rma/generic/maps/api';
import { provideCustomImageLoader } from '@rma/generic/ng/images';
import {
  appendToRmaApiUrl,
  appendToRmaBillingApiUrl,
  appendToRmaReviewsApiUrl,
  appendToRmaSurveyApiUrl,
  provideRmaApiUrl,
  provideRmaBaseUrl,
  provideRmaBillingApiUrl,
  provideRmaCdnUrl,
  provideRmaDashboardUrl,
  provideRmaEmailApiUrl,
  provideRmaFindMyApiUrl,
  provideRmaIdentityUrl,
  provideRmaImagesUrl,
  provideRmaReviewsApiUrl,
  provideRmaSocialApiUrl,
  provideRmaStaticImageUrl,
  provideRmaStaticUrl,
  provideRmaSurveyApiUrl,
} from '@rma/generic/ng/util';
import { LOCALITY_LOOKUP } from '@rma/generic/ui/controls/address';
import { provideRmaIcon } from '@rma/generic/ui/icon';
import { provideRmaUser } from '@rma/public/accounts/user';
import { provideShortlistMergePrompt } from '@rma/public/compare-agent/private/orc-shortlist-merge';
import { AutoSearchApiService } from '@rma/public/generic/api/auto-search';
import { provideRmaTranslation } from '@rma/public/generic/orc/translation';
import { Environment, IS_PRODUCTION, provideRmaLocale } from '@rma/public/generic/util/environment';
import {
  createAuthInterceptor,
  createJsonHeaderInterceptor,
  errorDecoratorInterceptor,
  provideRmaTokenProvider,
} from '@rma/public/generic/util/http';
import { httpCacheInterceptor } from '@rma/public/generic/util/http-browser-cache';
import { provideRmaMaterial } from '@rma/public/generic/util/material';
import { provideMetaInitializer } from '@rma/public/generic/util/meta-service';
import { provideRmaGoogleTracking } from '@rma/public/generic/util/tracking/analytics';
import { provideUuidGeneration } from '@rma/public/generic/util/uuid-generation';
import { provideLowerCaseUrlSerializer } from '@rma/public/site/public/orc-layout';
import { Angulartics2Module } from 'angulartics2';
import { environment as devEnvironment } from '../environment/environment';
import { abortErrorInterceptor } from './interceptors/abort.interceptor';
import { routes } from './routing/app.routes';
import { redirectNavigationErrorToErrorPage, routerConfig, scrollConfig } from './routing/routing.config';

declare let environment: Environment;

// Node has no injected `environment` global, so the server bundle reads the dev environment file.
const env = IS_PRODUCTION && typeof environment !== 'undefined' ? environment : devEnvironment;

export const commonConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserModule),
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    importProvidersFrom(Angulartics2Module.forRoot()),
    provideLowerCaseUrlSerializer(),
    provideMetaInitializer(),
    provideGoogleMaps(env.googleMapKey, env.countryCode),
    { provide: LOCALITY_LOOKUP, useExisting: AutoSearchApiService },
    provideRmaGoogleTracking(),
    provideRmaTokenProvider(),
    provideRmaIcon(),
    provideRmaLocale(),
    provideRmaMaterial(),
    provideRmaTranslation(),
    provideRmaUser(),
    provideShortlistMergePrompt(),
    provideUuidGeneration(),
    provideRouter(
      routes,
      withRouterConfig(routerConfig),
      withInMemoryScrolling(scrollConfig),
      withNavigationErrorHandler(redirectNavigationErrorToErrorPage),
    ),
    provideClientHydration(
      withHttpTransferCacheOptions({
        includePostRequests: true,
        includeRequestsWithAuthHeaders: true,
      }),
      withEventReplay(),
      withIncrementalHydration(),
    ),
    provideCustomImageLoader(),
    provideHttpClient(
      withInterceptors([
        abortErrorInterceptor,
        errorDecoratorInterceptor,
        httpCacheInterceptor({
          blacklist: ['/v2/users/me', '/users/me/*'],
          whitelist: ['/reviews/search/withaggregations'],
        }),
        createAuthInterceptor([appendToRmaApiUrl, appendToRmaReviewsApiUrl, appendToRmaBillingApiUrl, appendToRmaSurveyApiUrl]),
        createJsonHeaderInterceptor([appendToRmaApiUrl, appendToRmaReviewsApiUrl, appendToRmaBillingApiUrl]),
      ]),
      withFetch(),
    ),
    // setting up urls
    provideRmaApiUrl(),
    provideRmaBaseUrl(),
    provideRmaBillingApiUrl(),
    provideRmaCdnUrl(),
    provideRmaDashboardUrl(),
    provideRmaFindMyApiUrl(),
    provideRmaIdentityUrl(),
    provideRmaImagesUrl(),
    provideRmaReviewsApiUrl(),
    provideRmaSocialApiUrl(),
    provideRmaStaticImageUrl(),
    provideRmaStaticUrl(),
    provideRmaSurveyApiUrl(),
    provideRmaEmailApiUrl(),
  ],
};
